import { useEffect, useState } from "preact/hooks";
import { lang, t } from "../i18n";
import { logoUrl } from "../theme";
import * as S from "../state";

export function Setup() {
  const [view, setView] = useState<"welcome" | "manual">("welcome");
  const managed = S.mode.value === "managed";
  return (
    <div class="setup">
      <div class="panel">
        <Languages />
        <div class="brand">
          <img src="/app-icon.svg" alt="" />
          <div>
            <h1 style={{ margin: 0 }}>{t("setup.welcome")}</h1>
          </div>
        </div>
        {managed ? <Pairing /> : view === "manual" ? <Manual onBack={() => setView("welcome")} /> : <Welcome onManual={() => setView("manual")} />}
        <img class="footer-logo" src={logoUrl()} alt="MocTel" />
      </div>
    </div>
  );
}

function Languages() {
  const locked = S.settings.value["ui.language"]?.edit === false;
  if (locked) return null;
  return (
    <div class="langs">
      {(["fr", "en"] as const).map((l) => (
        <button type="button" class="btn link" aria-pressed={lang.value === l} style={{ fontWeight: lang.value === l ? 700 : 400 }} onClick={() => void S.setPrefs({ lang: l })}>
          {t(`lang.${l}`)}
        </button>
      ))}
    </div>
  );
}

function Welcome({ onManual }: { onManual: () => void }) {
  const cfg = S.site.value;
  return (
    <>
      <p class="muted">{t("setup.intro")}</p>
      <button type="button" class="btn primary wide" disabled={!cfg?.prov_url} onClick={() => void S.chooseManaged()}>
        {t("setup.activate_moctel")}
      </button>
      {cfg?.manual_setup && (
        <button type="button" class="btn link" onClick={onManual}>
          {t("setup.manual")}
        </button>
      )}
    </>
  );
}

function Pairing() {
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  const p = S.pairing.value;
  const left = p ? Math.max(0, Math.round((p.expiresAt - now) / 1000)) : 0;
  const enrolling = S.provState.value === "enrolling";
  return (
    <>
      <h2 style={{ marginTop: 0 }}>{enrolling ? t("setup.activating") : t("setup.code")}</h2>
      <p class="muted" style={{ margin: 0 }}>{t("setup.code_help")}</p>
      <div class="code" data-testid="pairing-code" aria-live="polite">{p?.code ?? "····-····"}</div>
      <div class="row between">
        <button
          type="button"
          class="btn soft"
          disabled={!p}
          onClick={() => {
            void navigator.clipboard?.writeText(p!.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? t("setup.copied") : t("setup.copy")}
        </button>
        {p && <span class="muted">{t("setup.new_code_in", `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`)}</span>}
      </div>
      {S.provNotice.value && <p class="warning-text" role="status">{S.provNotice.value}</p>}
      {showToken ? (
        <form
          class="row"
          onSubmit={(e) => {
            e.preventDefault();
            void S.enrollToken(token);
          }}
        >
          <input type="text" aria-label={t("setup.have_token")} placeholder="XXXX-XXXX-XXXX" value={token} onInput={(e) => setToken((e.target as HTMLInputElement).value)} />
          <button class="btn primary" type="submit" disabled={token.replace(/[\s-]/g, "").length !== 12}>
            {t("setup.activate")}
          </button>
        </form>
      ) : (
        <button type="button" class="btn link" onClick={() => setShowToken(true)}>
          {t("setup.have_token")}
        </button>
      )}
      <button type="button" class="btn link" onClick={() => void S.leaveSetup()}>
        {t("common.cancel")}
      </button>
    </>
  );
}

function Manual({ onBack }: { onBack: () => void }) {
  const [f, setF] = useState({ user: "", domain: "", auth_user: "", password: "", display_name: "" });
  const field = (k: keyof typeof f, label: string, type = "text", ph = "") => (
    <label class="field">
      <span>{label}</span>
      <input type={type} value={f[k]} placeholder={ph} autoComplete="off" onInput={(e) => setF({ ...f, [k]: (e.target as HTMLInputElement).value })} />
    </label>
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void S.saveManual(f);
      }}
    >
      {field("user", t("setup.user"))}
      {field("domain", t("setup.domain"), "text", t("setup.domain_ph"))}
      {field("auth_user", t("setup.auth_id"), "text", t("setup.optional"))}
      {field("password", t("setup.password"), "password")}
      {field("display_name", t("setup.display_name"), "text", t("setup.optional"))}
      <p class="muted" style={{ fontSize: "12px" }}>{t("setup.manual_note")}</p>
      <div class="row between">
        <button type="button" class="btn" onClick={onBack}>
          {t("common.back")}
        </button>
        <button type="submit" class="btn primary" disabled={!f.user || !f.domain || !f.password}>
          {t("common.save")}
        </button>
      </div>
    </form>
  );
}
